import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PeopleIcon from "@mui/icons-material/People";
import SchoolIcon from "@mui/icons-material/School";
import {
  Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import api from "../../api";
import { toast } from "react-toastify";
import AnalyticsPageShell, { CARD_SX } from "./AnalyticsPageShell";
import AnalyticsFiltersBar from "./AnalyticsFiltersBar";
import useAnalyticsFilters from "./useAnalyticsFilters";
import AnalyticsDataTable from "./AnalyticsDataTable";

const TILE_CONFIGS = [
  { key: "areas_covered", label: "Areas Covered", Icon: LocationOnIcon, iconBg: "#ede9fe", iconColor: "#7c3aed" },
  { key: "total_students", label: "Total Students", Icon: PeopleIcon, iconBg: "#dcfce7", iconColor: "#16a34a" },
  { key: "grammar_schools", label: "Grammar Schools", Icon: SchoolIcon, iconBg: "#dbeafe", iconColor: "#2563eb" },
  { key: "independent_schools", label: "Independent Schools", Icon: SchoolIcon, iconBg: "#dcfce7", iconColor: "#16a34a" },
  { key: "schools_mapped", label: "Schools on Map", Icon: SchoolIcon, iconBg: "#e0f2fe", iconColor: "#0284c7" },
];

function KpiTile({ label, value, Icon, iconBg, iconColor }) {
  return (
    <Paper sx={{ ...CARD_SX, display: "flex", alignItems: "center", gap: 2 }}>
      <Box sx={{ width: 52, height: 52, borderRadius: "50%", bgcolor: iconBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon sx={{ color: iconColor, fontSize: 26 }} />
      </Box>
      <Box>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>{(value ?? 0).toLocaleString()}</Typography>
      </Box>
    </Paper>
  );
}

function loadGoogleMaps(apiKey) {
  if (!apiKey) {
    return Promise.reject(new Error("Missing Google Maps API key"));
  }
  if (window.google?.maps) {
    return Promise.resolve(window.google.maps);
  }
  const existing = document.querySelector("script[data-geo-maps='1']");
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener("load", () => resolve(window.google.maps));
      existing.addEventListener("error", () => reject(new Error("Failed to load Google Maps")));
    });
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}`;
    script.async = true;
    script.defer = true;
    script.dataset.geoMaps = "1";
    script.onload = () => resolve(window.google.maps);
    script.onerror = () => reject(new Error("Failed to load Google Maps"));
    document.head.appendChild(script);
  });
}

function categoryColor(category) {
  const name = String(category || "").toLowerCase();
  if (name === "grammar" || name === "grammer") return "#2563eb";
  if (name === "independent") return "#16a34a";
  return "#64748b";
}

function GeographicMap({ mapConfig, rows, schools }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const [mapError, setMapError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const apiKey = mapConfig?.google_maps_api_key;
    if (!apiKey) {
      setMapError("Configure GOOGLE_MAPS_API_KEY (or GOOGLE_API_KEY) to show the map.");
      return undefined;
    }

    loadGoogleMaps(apiKey)
      .then((maps) => {
        if (cancelled || !mapRef.current) return;

        markersRef.current.forEach((marker) => marker.setMap(null));
        markersRef.current = [];

        const center = {
          lat: Number(mapConfig?.default_center?.lat ?? 54.5),
          lng: Number(mapConfig?.default_center?.lng ?? -2.5),
        };
        const zoom = Number(mapConfig?.default_zoom ?? 6);
        const ukBoundsConfig = mapConfig?.bounds || {
          south: 49.8,
          west: -8.6,
          north: 58.9,
          east: 2.0,
        };
        const ukBounds = new maps.LatLngBounds(
          { lat: Number(ukBoundsConfig.south), lng: Number(ukBoundsConfig.west) },
          { lat: Number(ukBoundsConfig.north), lng: Number(ukBoundsConfig.east) }
        );

        if (!mapInstanceRef.current) {
          mapInstanceRef.current = new maps.Map(mapRef.current, {
            center,
            zoom,
            minZoom: 5,
            maxZoom: 14,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: true,
            restriction: {
              latLngBounds: ukBounds,
              strictBounds: false,
            },
          });
        }

        const map = mapInstanceRef.current;
        const infoWindow = new maps.InfoWindow();
        const schoolBounds = new maps.LatLngBounds();
        let hasSchoolPoints = false;

        const isInUk = (lat, lng) =>
          lat >= Number(ukBoundsConfig.south) - 0.5 &&
          lat <= Number(ukBoundsConfig.north) + 0.5 &&
          lng >= Number(ukBoundsConfig.west) - 0.5 &&
          lng <= Number(ukBoundsConfig.east) + 0.5;

        (rows || []).forEach((row) => {
          if (row.latitude == null || row.longitude == null) return;
          const position = { lat: Number(row.latitude), lng: Number(row.longitude) };
          if (!isInUk(position.lat, position.lng)) return;
          const marker = new maps.Marker({
            map,
            position,
            title: row.location,
            zIndex: 2,
            icon: {
              path: maps.SymbolPath.CIRCLE,
              scale: 9,
              fillColor: "#7c3aed",
              fillOpacity: 0.95,
              strokeColor: "#ffffff",
              strokeWeight: 2,
            },
          });
          marker.addListener("click", () => {
            infoWindow.setContent(
              `<div style="min-width:160px">
                <strong>${row.location}</strong><br/>
                Students: ${row.student_count ?? 0}<br/>
                Teachers: ${row.teacher_count ?? 0}<br/>
                Groups: ${row.batches_running ?? 0}
              </div>`
            );
            infoWindow.open({ map, anchor: marker });
          });
          markersRef.current.push(marker);
        });

        (schools || []).forEach((school) => {
          if (school.latitude == null || school.longitude == null) return;
          const position = { lat: Number(school.latitude), lng: Number(school.longitude) };
          if (!isInUk(position.lat, position.lng)) return;
          const color = categoryColor(school.category);
          const marker = new maps.Marker({
            map,
            position,
            title: school.name,
            zIndex: 1,
            icon: {
              path: maps.SymbolPath.CIRCLE,
              scale: 5,
              fillColor: color,
              fillOpacity: 0.95,
              strokeColor: "#ffffff",
              strokeWeight: 1,
            },
          });
          marker.addListener("click", () => {
            infoWindow.setContent(
              `<div style="min-width:180px">
                <strong>${school.name}</strong><br/>
                Region: ${school.region || "—"}<br/>
                Category: ${school.category || "—"}
              </div>`
            );
            infoWindow.open({ map, anchor: marker });
          });
          markersRef.current.push(marker);
          schoolBounds.extend(position);
          hasSchoolPoints = true;
        });

        if (mapConfig?.focus_region && hasSchoolPoints) {
          map.fitBounds(schoolBounds, { top: 40, right: 40, bottom: 40, left: 40 });
          maps.event.addListenerOnce(map, "bounds_changed", () => {
            if (map.getZoom() > 10) map.setZoom(10);
            if (map.getZoom() < 7) map.setZoom(7);
          });
        } else if (mapConfig?.focus_region) {
          map.setCenter(center);
          map.setZoom(zoom);
        } else {
          map.fitBounds(ukBounds, { top: 24, right: 24, bottom: 24, left: 24 });
          maps.event.addListenerOnce(map, "bounds_changed", () => {
            if (map.getZoom() > 6) map.setZoom(6);
          });
        }

        setMapError(null);
      })
      .catch((err) => {
        if (!cancelled) setMapError(err.message || "Failed to load Google Maps");
      });

    return () => {
      cancelled = true;
    };
  }, [mapConfig, rows, schools]);

  if (mapError) {
    return <Alert severity="warning" sx={{ mb: 2 }}>{mapError}</Alert>;
  }

  return (
    <Paper sx={{ ...CARD_SX, mb: 2, p: 0, overflow: "hidden" }}>
      <Box sx={{ px: 2, pt: 2, pb: 1, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Schools &amp; Areas Map</Typography>
        <Typography variant="caption" color="text.secondary">
          Purple = teaching areas · Blue = Grammar · Green = Independent · Grey = other
        </Typography>
      </Box>
      <Box ref={mapRef} sx={{ width: "100%", height: { xs: 420, md: 560 } }} />
    </Paper>
  );
}

export default function GeographicAnalytics() {
  const [courseId, setCourseId] = useState("");
  const [academicCourseId, setAcademicCourseId] = useState("");
  const [mode, setMode] = useState("");
  const [regionId, setRegionId] = useState("");
  const [category, setCategory] = useState("");
  const [summary, setSummary] = useState({});
  const [rows, setRows] = useState([]);
  const [schools, setSchools] = useState([]);
  const [regions, setRegions] = useState([]);
  const [regionSummary, setRegionSummary] = useState([]);
  const [mapConfig, setMapConfig] = useState(null);
  const [loading, setLoading] = useState(false);

  const { courses, academicCourses } = useAnalyticsFilters({ courseId, academicCourseId });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = { include_schools: 1 };
      if (courseId) params.course_id = courseId;
      if (academicCourseId) params.academic_course_id = academicCourseId;
      if (mode) params.mode = mode;
      if (regionId) params.region_id = regionId;
      if (category) params.category = category;
      const res = await api.get("admin/analytics/geographic", { params });
      const d = res.data?.data || {};
      setSummary(d.summary || {});
      setRows(d.rows || []);
      setSchools(d.schools || []);
      setRegions(d.regions || []);
      setRegionSummary(d.region_summary || []);
      setMapConfig(d.map || null);
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to load geographic data");
      setSummary({});
      setRows([]);
      setSchools([]);
      setRegions([]);
      setRegionSummary([]);
      setMapConfig(null);
    } finally {
      setLoading(false);
    }
  }, [courseId, academicCourseId, mode, regionId, category]);

  useEffect(() => { load(); }, [load]);

  const areaColumns = [
    { key: "location", label: "Area" },
    { key: "student_count", label: "Students" },
    { key: "teacher_count", label: "Teachers" },
    { key: "batches_running", label: "Groups Running" },
  ];

  const schoolColumns = [
    { key: "name", label: "School" },
    { key: "region", label: "Region" },
    { key: "category", label: "Category" },
    { key: "latitude", label: "Latitude" },
    { key: "longitude", label: "Longitude" },
  ];

  const schoolTableRows = useMemo(
    () =>
      (schools || []).map((s) => ({
        ...s,
        latitude: s.latitude != null ? Number(s.latitude).toFixed(4) : "—",
        longitude: s.longitude != null ? Number(s.longitude).toFixed(4) : "—",
        region: s.region || "—",
        category: s.category || "—",
      })),
    [schools]
  );

  const regionChartData = useMemo(
    () =>
      (regionSummary || []).map((r) => ({
        region: r.region,
        Grammar: r.grammar,
        Independent: r.independent,
        Total: r.total,
      })),
    [regionSummary]
  );

  return (
    <AnalyticsPageShell
      title="Geographical Insights"
      subtitle="School locations by region, with student and teacher area analytics."
      filters={
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}>
          <AnalyticsFiltersBar
            courses={courses}
            academicCourses={academicCourses}
            plans={[]}
            courseId={courseId}
            setCourseId={setCourseId}
            showAcademicCourse={false}
            academicCourseId={academicCourseId}
            setAcademicCourseId={setAcademicCourseId}
            showPlan={false}
            showMode
            mode={mode}
            setMode={setMode}
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Region</InputLabel>
                <Select
                  label="Region"
                  value={regionId || ""}
                  onChange={(e) => setRegionId(e.target.value || "")}
                >
                  <MenuItem value="">All regions (UK)</MenuItem>
                  {regions.map((r) => (
                    <MenuItem key={r.id} value={r.id}>
                      {r.name} ({r.total})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>School category</InputLabel>
                <Select
                  label="School category"
                  value={category || ""}
                  onChange={(e) => setCategory(e.target.value || "")}
                >
                  <MenuItem value="">All categories</MenuItem>
                  <MenuItem value="grammar">Grammar</MenuItem>
                  <MenuItem value="independent">Independent</MenuItem>
                  <MenuItem value="both">Both</MenuItem>
                  <MenuItem value="other">Other / uncategorised</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          {regions.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              <Chip
                label="All UK"
                color={!regionId ? "primary" : "default"}
                variant={!regionId ? "filled" : "outlined"}
                onClick={() => setRegionId("")}
                sx={{ fontWeight: 600 }}
              />
              {regions.map((r) => (
                <Chip
                  key={r.id}
                  label={`${r.name}: ${r.grammar}G · ${r.independent}I`}
                  color={String(regionId) === String(r.id) ? "primary" : "default"}
                  variant={String(regionId) === String(r.id) ? "filled" : "outlined"}
                  onClick={() => setRegionId(String(r.id))}
                  sx={{ fontWeight: 600 }}
                />
              ))}
            </Box>
          )}
        </Box>
      }
    >
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {TILE_CONFIGS.map(({ key, label, Icon, iconBg, iconColor }) => (
              <Grid item xs={12} sm={6} md={4} lg key={key} sx={{ flexGrow: 1, minWidth: { lg: "18%" } }}>
                <KpiTile label={label} value={summary[key]} Icon={Icon} iconBg={iconBg} iconColor={iconColor} />
              </Grid>
            ))}
          </Grid>

          <GeographicMap mapConfig={mapConfig} rows={rows} schools={schools} />

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <Paper sx={CARD_SX}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>Schools by Region</Typography>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={regionChartData} margin={{ top: 0, right: 8, left: -16, bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="region" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={60} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="Grammar" fill="#2563eb" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="Independent" fill="#16a34a" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={CARD_SX}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>Student Numbers by Area</Typography>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={rows} margin={{ top: 0, right: 8, left: -16, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="location" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="student_count" name="Students" fill="#7c3aed" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          </Grid>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Schools on Map ({schools.length})
          </Typography>
          <Box sx={{ mb: 3 }}>
            <AnalyticsDataTable columns={schoolColumns} rows={schoolTableRows} loading={false} />
          </Box>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Area Summary</Typography>
          <AnalyticsDataTable columns={areaColumns} rows={rows} loading={false} />
        </>
      )}
    </AnalyticsPageShell>
  );
}
